import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import _map from 'lodash/map';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import ActionModal from '../../containers/modals/url';
import { PowerChatFlowExtraDataT } from '../PowerChatFlow';

export type FlowChatNodeURLTriggerPayloadT = {
  defaultUrl: string;
  urls: Record<string, string>;
};

type FlowChatNodeURLTriggerTypeT = FlowNodeTypes.URL;
export type FlowChatNodeURLTriggerT = NodeT<
  FlowChatNodeURLTriggerTypeT,
  FlowChatNodeURLTriggerPayloadT
>;

const portTranslator = (node: FlowChatNodeURLTriggerT): RegisterPortT[] => {
  return [
    {
      id: uuid(),
      type: 'out',
      label: 'Default',
      connection: node.payload.defaultUrl || uuid(),
    },
    ..._map(
      node.payload.urls,
      (connection: string, label: string): RegisterPortT => ({
        id: uuid(),
        type: 'out',
        label,
        connection,
      }),
    ),
  ];
};

export class URLTriggerNode extends NodeTypeComponent<
  FlowChatNodeURLTriggerT,
  PowerChatFlowExtraDataT
>() {
  static icon: IconNames = 'url-trigger';
  static label = 'URL Trigger';
  static color = flowNodeColor.purple;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.URL };
  static updateConnection = (
    node: FlowChatNodeURLTriggerT,
    port: InternalPortT,
    newConnection: string,
  ): FlowChatNodeURLTriggerT => {
    if (!port) return node;

    const newNode = { ...node };
    if (port.label === 'Default') {
      newNode.payload.defaultUrl = newConnection;
    } else if (port.label && newNode.payload.urls[port.label]) {
      newNode.payload.urls[port.label] = newConnection;
    }

    return newNode;
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    const {
      node: {
        payload: { urls },
      },
    } = this.props;
    const urlCount = Object.keys(urls).length;
    const text = urlCount === 1 ? 'URL' : `URLs`;

    return (
      <FlowNode
        color={URLTriggerNode.color}
        icon={URLTriggerNode.icon}
        label={URLTriggerNode.label}
        text={
          <strong>
            {urlCount} {text}
          </strong>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
